import type { Experiment } from 'types';
import type { CombinationReportResponse } from 'statisticsdb/types';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useQueryState } from 'hooks/useQueryState';
import { useClient } from 'hooks/useClient';
import { useWebsites } from '@/state/useWebsites';
import { config } from 'config';

const GOALS = ['Conversions', 'Revenue'] as const;

/** The selection is a comma-separated query-string value, so it can hold anything. */
const parseExperimentIds = (selectedIds: string): number[] =>
  selectedIds
    .split(',')
    .filter(Boolean)
    .map(Number)
    .filter((n) => !isNaN(n));

const isReportable = (experiment: Experiment) => experiment.status === 'Running' || experiment.status === 'Ended';

export const useCombinationReport = (allExperiments: Experiment[] | undefined) => {
  const statisticsClient = useClient(config.STATISTICS_URL);
  const { website } = useWebsites();
  const websiteId = website?.id;

  const [selectedIds, setSelectedIds] = useQueryState<string>('experiments', '');
  const [currency, setCurrency] = useQueryState<string>('currency', 'nill');
  const [goal, setGoal] = useQueryState<string>('goal', GOALS[0]);

  const experimentIds = parseExperimentIds(selectedIds);
  const availableExperiments = allExperiments?.filter(isReportable) ?? [];

  // The selection lives in the query string and so survives a website switch,
  // but experiment ids belong to one website. Sending a stale id makes the API
  // reject the whole request ("All experiments must belong to the same
  // website"), which surfaced as a blanket "failed to load". Only request ids
  // that exist on the current website, and prune the rest from the URL once the
  // experiment list has actually loaded.
  const selectableIds = new Set(availableExperiments.map((e) => e.id));
  const validIds = allExperiments ? experimentIds.filter((id) => selectableIds.has(id)) : experimentIds;
  const validSelection = validIds.join(',');

  useEffect(() => {
    if (!allExperiments) return;
    if (validIds.length !== experimentIds.length) setSelectedIds(validSelection);
  }, [allExperiments, validSelection, experimentIds.length, validIds.length]);

  const isReady = validIds.length >= 2 && !!websiteId;

  const { data, isLoading, error } = useQuery<CombinationReportResponse>({
    queryKey: ['COMBINATION_REPORT', websiteId, validSelection, currency],
    enabled: isReady,
    queryFn: () =>
      statisticsClient.get(`/combination-report/${websiteId}?experimentIds=${validSelection}&currency=${currency}`),
  });

  const addExperiment = (id: number) => {
    if (experimentIds.includes(id)) return;
    const next = [...experimentIds, id].join(',');
    setSelectedIds(next);
  };

  const removeExperiment = (id: number) => {
    const next = experimentIds.filter((eid) => eid !== id).join(',');
    setSelectedIds(next);
  };

  return {
    GOALS,
    data,
    isLoading,
    error,
    experimentIds: validIds,
    availableExperiments,
    addExperiment,
    removeExperiment,
    currency,
    setCurrency,
    goal,
    setGoal,
    isReady,
    // The period every selected experiment was randomising at once, which bounds
    // everything counted. Null when they never overlapped.
    window: data?.window ?? null,
    interactionAvailable: data?.interactionAvailable ?? false,
    reason: data?.reason ?? null,
  };
};
